import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { Box, Button, Typography, Link } from "@mui/material";
import { useSession } from "next-auth/react";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import RegisterModal from "@/components/core/register-modal";

interface OfferzPlusProps {
  loyaltyPoints: number;
  onCheckout?: boolean;
  lang: string;
}

const OfferzPlus: React.FC<OfferzPlusProps> = ({ loyaltyPoints, onCheckout = false, lang }) => {
  const { i18n } = useLingui();
  const { data: session, status } = useSession();

  const [isGuest, setIsGuest] = useState<boolean>(false);
  const isUnauthenticated = status === "unauthenticated";
  const isEnrolledInLoyalty = session?.user.enrolledInLoyalty === 1;
  const isNotEnrolledInLoyalty = session?.user.enrolledInLoyalty === 0;
  const [openRegisterModal, setOpenRegisterModal] = useState<boolean>(false);
  const handleCloseRegisterModal = () => {
    setOpenRegisterModal(false);
  };
  useEffect(() => {
    const is_guest = session?.user.is_guest || "";
    setIsGuest(is_guest === "1");
  }, [session]);
  const renderPointsMessage = () => {
    if (!onCheckout) {
      return (
        <Typography sx={{ fontWeight: 400, fontSize: { xs: "11px", md: "13px" } }}>
          <Trans>
            For this article, you would get
            <span className="px-[3px] font-bold">{loyaltyPoints}</span>
            points.
          </Trans>
        </Typography>
      );
    }

    if (onCheckout && isEnrolledInLoyalty) {
      return (
        <Typography sx={{ fontWeight: 400, fontSize: { xs: "11px", md: "13px" } }}>
          <Trans>
            You will receive
            <span className="px-[3px] font-bold">{loyaltyPoints}</span>
            points for this purchase.
          </Trans>
        </Typography>
      );
    }

    if (onCheckout && isNotEnrolledInLoyalty) {
      return (
        <Typography sx={{ fontWeight: 400, fontSize: "13px", lineHeight: 1.5 }}>
          <Trans>
            You would receive
            <span className="px-[3px] font-bold">{loyaltyPoints}</span>
            points for this purchase.
          </Trans>
          <span style={{ display: "inline-block", marginLeft: "3px" }}>
            {isGuest && (
              <Box>
                <RegisterModal
                  open={openRegisterModal}
                  handleClose={handleCloseRegisterModal}
                  lang={lang}
                />
                <Link
                  onClick={() => setOpenRegisterModal(true)}
                  underline="always"
                  sx={{
                    color: "primary.main",
                    textDecoration: "none",
                    fontWeight: 500,
                    textAlign: "center",
                    cursor: "pointer",
                    lineHeight: 1,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  <Trans>Create an account</Trans>
                </Link>
              </Box>
            )}
            {!isGuest && (
              <Link href="/loyalty">
                <span className="text-[#FFA000] hover:underline">
                  <Trans>Learn more</Trans>
                </span>
              </Link>
            )}
          </span>
        </Typography>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "10px",
        bgcolor: "#fff3d7",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
      data-testid="offerz-plus-container"
    >
      <Box sx={{ display: "flex", gap: { xs: 1, md: 2 } }}>
        <Box sx={{ minWidth: { xs: 32, md: 40 } }}>
          <Image
            loading="lazy"
            src="/coin.png"
            width={40}
            height={40}
            alt={t(i18n)`Coin`}
            className="h-8 w-8 md:h-10 md:w-10"
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
            <Trans>Collect Offerz Plus points</Trans>
          </Typography>
          {renderPointsMessage()}
        </Box>
      </Box>
      {(isUnauthenticated || (isNotEnrolledInLoyalty && !onCheckout)) && (
        <Link href="/loyalty">
          <Button
            variant="contained"
            sx={{
              p: "4px 15px",
              width: "fit-content",
              height: "auto",
              fontWeight: 600,
              bgcolor: "#ffb900",
              boxShadow: "none",
              fontSize: "14px !important",
              padding: "6px 10px",
            }}
            data-testid="participate-button"
          >
            <Trans>Participate now</Trans>
          </Button>
        </Link>
      )}
    </Box>
  );
};

export default React.memo(OfferzPlus);
